import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import StudentBadges from "@/components/app/StudentBadges";
import { toast } from "sonner";
import { api, API, BACKEND_URL, downloadPdf } from "@/lib/api";
import { fmtTime12, isLate } from "@/lib/utils";
import ThemedBackground from "@/components/app/ThemedBackground";
import PushToggle from "@/components/app/PushToggle";
import NotificationCenter from "@/components/app/NotificationCenter";
import PushPrompt from "@/components/app/PushPrompt";
import SiteFooter from "@/components/app/SiteFooter";
import LogoSplash from "@/components/app/LogoSplash";
import { StudentResourcesPanel, StudentTemarioPanel } from "@/components/app/StudentResourcesTemario";
import { applyTheme, resetThemeToStock } from "@/lib/themes";
import { KeyRound, ScanLine, Plus, Trash2, CalendarDays, Wallet, Download, CheckCircle2, AlertCircle, ArrowLeft, GraduationCap, Building2, Upload, Clock, Car, Footprints, Bike, LogOut, X, Copy, Presentation, Megaphone, FolderOpen, Paperclip, ExternalLink, Youtube, ClipboardCheck, Send, Loader2, CalendarClock, FileText, Award, ChevronDown, UtensilsCrossed } from "lucide-react";

const LS_KEY = "uaicard_parent_children";
const loadChildren = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const saveChildren = (list) => localStorage.setItem(LS_KEY, JSON.stringify(list));
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MESES_LARGO = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const fmtDueDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} de ${MESES_LARGO[m - 1]} de ${y}`;
};

function CopyableField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiada`);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("No se pudo copiar"); }
  };
  return (
    <div className="flex items-center gap-2 text-[12px] text-nardo-600">
      <span className="font-mono">{label}: {value}</span>
      <button type="button" onClick={copy} data-testid={`copy-${label.toLowerCase()}`}
        className="inline-flex items-center gap-1 text-[10px] text-navy hover:text-navy-light border border-nardo-200 hover:border-navy rounded px-1.5 py-0.5 transition-colors">
        <Copy className="w-3 h-3" /> {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
}

function AddChildForm({ onAdded, onCancel, canCancel, adding }) {
  const [uid, setUid] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adding) return;
    const demoUid = new URLSearchParams(window.location.search).get("demo");
    if (!demoUid) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/parent/verify", { uid: demoUid.trim() });
        onAdded({ token: data.token, student: data.student }, false);
      } catch (err) { setError("No se pudo abrir la demo de padres."); setLoading(false); }
    })();
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/parent/verify", { uid: uid.trim() });
      onAdded({ token: data.token, student: data.student }, adding);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo verificar. Revisa el UID.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-nardo-200 rounded-xl p-6 sm:p-8 shadow-lg max-w-md w-full" data-testid="parent-login-card">
      <div className="absolute -top-px left-0 right-0 h-[2px] bg-navy" />
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="w-4 h-4 text-navy" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-navy">Acceso de tutores</div>
      </div>
      <h2 className="font-display font-bold text-2xl text-navy mb-1">{adding ? "Agregar otro hijo/a" : "Consulta a tu hijo/a"}</h2>
      <p className="text-sm text-nardo-500 mb-6">Ingresa el <strong>UID</strong> del alumno (el de su tarjeta/credencial). Te lo proporciona el colegio.{adding ? " Se guardará junto a tus otros hijos en este dispositivo." : ""}</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">UID del alumno</label>
          <input
            data-testid="parent-uid-input"
            value={uid}
            onChange={(e) => setUid(e.target.value.toUpperCase())}
            placeholder="EJ. A200"
            className="mt-1 w-full text-center font-mono tracking-[0.3em] text-2xl uppercase bg-nardo-50 border-2 border-dashed border-nardo-300 p-4 rounded-lg focus:border-navy outline-none"
          />
        </div>
        {error && <div className="text-sm text-red-600" data-testid="parent-login-error">{error}</div>}
        <button type="submit" disabled={loading || !uid} data-testid="parent-verify-button"
          className="w-full bg-navy hover:bg-navy-light text-white py-3 rounded-md transition-colors font-medium disabled:opacity-50">
          {loading ? "Verificando…" : (adding ? "Guardar hijo/a" : "Ver a mi hijo/a")}
        </button>
        {canCancel && (
          <button type="button" onClick={onCancel} className="w-full text-xs uppercase tracking-[0.25em] text-nardo-500 hover:text-navy" data-testid="parent-add-cancel">
            Cancelar
          </button>
        )}
      </form>
    </div>
  );
}

function SummaryCard({ att, stmt }) {
  if (!att || !stmt) {
    return <div className="bg-white border border-nardo-200 rounded-xl p-5 mb-6 h-24 animate-pulse" data-testid="parent-summary-loading" />;
  }
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthDays = (att.days || []).filter((d) => d.date.startsWith(ym));
  const present = monthDays.length;
  const lateDays = monthDays.filter((d) => d.first_in && isLate(d.first_in, att.late_after)).length;
  // días hábiles (L-V) transcurridos del mes hasta hoy
  let weekdays = 0;
  for (let day = 1; day <= now.getDate(); day++) {
    const wd = new Date(now.getFullYear(), now.getMonth(), day).getDay();
    if (wd !== 0 && wd !== 6) weekdays++;
  }
  const pct = weekdays > 0 ? Math.min(100, Math.round((present / weekdays) * 100)) : 0;
  const pctColor = pct >= 90 ? "text-emerald-600" : pct >= 75 ? "text-orange-500" : "text-red-600";
  const balColor = stmt.balance > 0 ? "text-orange-600" : "text-emerald-600";

  return (
    <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="parent-summary">
      <div className="bg-white border border-nardo-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Asistencia del mes</div>
        <div className={`font-display font-extrabold text-3xl mt-1 ${pctColor}`} data-testid="summary-attendance-pct">{pct}%</div>
        <div className="text-xs text-nardo-400 mt-0.5">{present} de {weekdays} días hábiles</div>
      </div>
      <div className="bg-white border border-nardo-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Puntualidad</div>
        <div className="font-display font-extrabold text-3xl mt-1 text-navy">{present - lateDays}<span className="text-base text-nardo-400 font-normal"> a tiempo</span></div>
        <div className="text-xs text-nardo-400 mt-0.5">{lateDays} con retardo este mes</div>
      </div>
      <div className="bg-white border border-nardo-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Saldo total</div>
        <div className={`font-display font-extrabold text-3xl mt-1 ${balColor}`} data-testid="summary-balance">{money(stmt.balance)}</div>
        <div className="text-xs text-nardo-400 mt-0.5">{stmt.balance > 0 ? "Adeudo acumulado" : "Al corriente"}</div>
      </div>
    </div>
  );
}

function AttendanceSection({ data, token, name }) {
  const dl = () => downloadPdf("/parent/attendance-pdf", { params: { pt: token, period: "mes" }, filename: `asistencia_${name}.pdf` });

  return (
    <div className="bg-white border border-nardo-200 rounded-lg p-5 sm:p-6" data-testid="parent-attendance-section">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-navy" />
          <h3 className="font-display font-semibold text-lg text-navy">Asistencia</h3>
        </div>
        <button onClick={dl} data-testid="parent-attendance-pdf" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-navy border border-nardo-300 hover:border-navy px-3 py-2 rounded-md transition-colors">
          <Download className="w-3.5 h-3.5" /> PDF del mes
        </button>
      </div>
      {!data ? (
        <div className="py-8 text-center text-sm text-nardo-400">Cargando…</div>
      ) : data.days.length === 0 ? (
        <div className="py-8 text-center text-sm text-nardo-400">Sin registros recientes.</div>
      ) : (
        <div className="divide-y divide-nardo-100">
          {data.days.map((d) => {
            const late = d.first_in && isLate(d.first_in, data.late_after);
            return (
              <div key={d.date} className="py-2.5 flex items-center justify-between text-sm" data-testid="parent-att-row">
                <span className="text-navy font-medium">{new Date(d.date + "T12:00:00").toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })}</span>
                <div className="flex items-center gap-4 font-mono text-xs">
                  <span className={d.first_in ? (late ? "text-orange-600" : "text-emerald-700") : "text-nardo-300"}>↓ {d.first_in ? fmtTime12(d.first_in) : "—"}</span>
                  <span className={d.last_out ? "text-blue-600" : "text-nardo-300"}>↑ {d.last_out ? fmtTime12(d.last_out) : "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatementSection({ data, token, paymentInfo, onDone }) {
  const accounts = (paymentInfo && paymentInfo.deposit_accounts) || [];
  const concepts = (paymentInfo && paymentInfo.fee_concepts) || [];
  const [trConcept, setTrConcept] = useState("");
  const [trAmount, setTrAmount] = useState("");
  const [trFile, setTrFile] = useState(null);
  const [trBusy, setTrBusy] = useState(false);
  const trRef = useRef(null);

  const onTrConcept = (name) => {
    setTrConcept(name);
    const fc = concepts.find((c) => c.name === name);
    if (fc && fc.amount > 0) setTrAmount(String(fc.amount));
  };
  const maxAmt = Number((paymentInfo && paymentInfo.max_payment_amount) || 0);
  const submitTransfer = async () => {
    if (!trConcept) { toast.error("Elige un concepto"); return; }
    const amt = parseFloat(trAmount);
    if (!(amt > 0)) { toast.error("Ingresa un monto válido"); return; }
    if (maxAmt > 0 && amt > maxAmt) { toast.error(`El monto máximo permitido por pago es ${money(maxAmt)}.`); return; }
    if (!trFile) { toast.error("Adjunta el comprobante de tu transferencia"); return; }
    setTrBusy(true);
    try {
      const fd = new FormData();
      fd.append("concept", trConcept);
      fd.append("amount", amt);
      fd.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
      fd.append("file", trFile);
      await api.post("/parent/transfer", fd, { headers: { "X-Parent-Token": token, "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Comprobante enviado. Tu pago quedó pendiente de verificación por el colegio.", { duration: 8000 });
      setTrConcept(""); setTrAmount(""); setTrFile(null);
      if (trRef.current) trRef.current.value = "";
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar el comprobante"); }
    finally { setTrBusy(false); }
  };

  const statusBadge = (s) => s === "paid"
    ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Al corriente</span>
    : <span className="inline-flex items-center gap-1 text-orange-600 text-xs"><AlertCircle className="w-3.5 h-3.5" /> {s === "partial" ? "Pago parcial" : "Pendiente"}</span>;

  return (
    <div className="bg-white border border-nardo-200 rounded-lg p-5 sm:p-6" data-testid="parent-statement-section">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-4 h-4 text-navy" />
        <h3 className="font-display font-semibold text-lg text-navy">Estado de cuenta</h3>
        {data?.plan_label && <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold bg-navy/10 text-navy px-2.5 py-1 rounded-full" data-testid="parent-plan-badge">Plan: {data.plan_label}{data?.student?.level ? ` · ${data.student.level}` : ""}</span>}
      </div>
      {!data ? (
        <div className="py-8 text-center text-sm text-nardo-400">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-nardo-50 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Colegiatura ({data.month})</div>
              <div className="font-display font-bold text-navy text-lg mt-1">{money(data.expected)}</div>
            </div>
            <div className="bg-nardo-50 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Pagado</div>
              <div className="font-display font-bold text-emerald-700 text-lg mt-1">{money(data.paid)}</div>
            </div>
            <div className="bg-nardo-50 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Saldo total</div>
              <div className={`font-display font-bold text-lg mt-1 ${data.balance > 0 ? "text-orange-600" : "text-navy"}`}>{money(data.balance)}</div>
            </div>
          </div>
          <div className="mb-4" data-testid="parent-statement-status">{statusBadge(data.status)}</div>

          {Array.isArray(data.months_due) && data.months_due.length > 0 && (data.has_overdue || data.months_due.length > 1 || data.credit_balance > 0.009 || data.months_due.some((m) => m.credit_applied > 0.009)) && (
            <div className="mb-5 rounded-md border border-red-200 bg-red-50 p-3 text-[12px]" data-testid="parent-months-due">
              <div className="font-semibold text-red-700 flex items-center gap-1.5 mb-1.5"><AlertCircle className="w-4 h-4" /> Meses por pagar</div>
              <div className="space-y-1">
                {data.months_due.map((m) => (
                  <div key={m.period} className="flex justify-between items-start gap-2">
                    <span className="text-red-700 capitalize">
                      {m.overdue ? `Adeudo pendiente de ${m.label}` : m.label}
                      {m.overdue && <span className="ml-1.5 text-[9px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded-full align-middle uppercase tracking-wide">Vencido</span>}
                      {m.is_current && !m.overdue && m.balance > 0.009 && <span className="ml-1.5 text-[9px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded-full align-middle uppercase tracking-wide">Mes actual</span>}
                      {m.balance <= 0.009 && m.credit_applied > 0.009 && <span className="ml-1.5 text-[9px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded-full align-middle uppercase tracking-wide">Cubierto</span>}
                      {m.credit_applied > 0.009 && (
                        <div className="text-[11px] text-emerald-700 font-medium mt-0.5 normal-case" data-testid={`parent-credit-applied-${m.period}`}>
                          Se aplicó {money(m.credit_applied)} de saldo a favor{m.balance > 0.009 ? "" : " (mes cubierto)"}
                        </div>
                      )}
                    </span>
                    <span className="font-semibold text-red-700 whitespace-nowrap">{money(m.balance)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between border-t border-red-200 mt-2 pt-1.5 font-bold text-red-800"><span>Total adeudado</span><span data-testid="parent-total-due">{money(data.total_due != null ? data.total_due : data.balance)}</span></div>
              {data.credit_balance > 0.009 && (
                <div className="flex justify-between mt-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="parent-credit-balance"><span>Saldo a favor disponible</span><span>{money(data.credit_balance)}</span></div>
              )}
            </div>
          )}

          {data.credit_balance > 0.009 && !(Array.isArray(data.months_due) && data.months_due.length > 0 && (data.has_overdue || data.months_due.length > 1)) && (
            <div className="mb-5 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12px] flex justify-between items-center" data-testid="parent-credit-banner">
              <span className="font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Saldo a favor disponible</span>
              <span className="font-bold text-emerald-700">{money(data.credit_balance)}</span>
            </div>
          )}

          {data.plan_label && data.plan_fee > 0 && (
            <div className="mb-5 rounded-md border border-navy/15 bg-navy/[0.03] p-3 text-[12px] space-y-1" data-testid="parent-plan-detail">
              <div className="flex justify-between text-navy font-semibold"><span>Plan {data.plan_label}{data?.student?.level ? ` · ${data.student.level}` : ""}</span><span data-testid="parent-plan-fee">{money(data.plan_fee)}/mes</span></div>
              {data.scholarship_pct > 0 && <div className="flex justify-between text-emerald-700"><span>Beca {data.scholarship_pct}%</span><span data-testid="parent-scholarship-amount">− {money(data.scholarship_amount)}</span></div>}
              {data.scholarship_pct > 0 && <div className="flex justify-between text-navy font-semibold border-t border-navy/10 pt-1"><span>Colegiatura con beca</span><span>{money(Math.max(0, (data.plan_fee || 0) - (data.scholarship_amount || 0)))}</span></div>}
            </div>
          )}

          {data.not_billable ? (
            <div className="mb-5 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-700 font-medium" data-testid="parent-no-billing">
              Este mes no se cobra colegiatura.
            </div>
          ) : (data.due_day || data.early_discount > 0 || data.late_fee > 0) && (
            <div className="mb-5 rounded-md border border-nardo-100 bg-nardo-50/60 p-3 text-[12px] space-y-1" data-testid="parent-tuition-breakdown">
              {data.due_date
                ? <div className="flex justify-between text-nardo-600"><span>Fecha límite de pago</span><span className="font-semibold text-navy" data-testid="parent-due-date">{fmtDueDate(data.due_date)}</span></div>
                : (data.due_day && <div className="flex justify-between text-nardo-600"><span>Fecha límite de pago</span><span className="font-medium">día {data.due_day}</span></div>)}
              {data.base > 0 && <div className="flex justify-between text-nardo-600"><span>Colegiatura</span><span>{money(data.base)}</span></div>}
              {data.early_discount > 0 && <div className="flex justify-between text-emerald-700"><span>Descuento por pago anticipado</span><span>− {money(data.early_discount)}</span></div>}
              {data.late_fee > 0 && (() => {
                const isPct = data.late_fee_type === "pct";
                const mode = data.late_fee_mode || "day";
                const isWeek = mode === "week";
                const isMonth = mode === "month";
                const fallback = isWeek ? Math.max(1, Math.ceil((data.late_fee_days || 0) / 7)) : (data.late_fee_days || 0);
                const units = data.late_fee_units != null ? data.late_fee_units : fallback;
                const unitWord = isMonth ? (units === 1 ? "mes" : "meses") : isWeek ? (units === 1 ? "semana" : "semanas") : (units === 1 ? "día" : "días");
                const perWord = isMonth ? "mensual" : isWeek ? "semanal" : "diario";
                const perUnit = isMonth ? "mes" : isWeek ? "semana" : "día";
                const baseWord = data.late_fee_base === "net" ? "de la colegiatura (con beca)" : "de la colegiatura";
                const rule = isPct ? `${data.late_fee_rate}% ${perWord} ${baseWord}` : `${money(data.late_fee_rate)} por ${perUnit}`;
                const capped = data.late_fee_cap > 0 && data.late_fee >= data.late_fee_cap - 0.01;
                return (
                  <div data-testid="parent-latefee-line">
                    <div className="flex justify-between text-red-600"><span>Recargo por mora</span><span>+ {money(data.late_fee)}</span></div>
                    {mode === "flat"
                      ? <div className="text-[10px] text-red-500/80 leading-tight">Recargo único al vencer · {isPct ? `${data.late_fee_rate}% ${baseWord}` : money(data.late_fee_rate)}{capped ? " · tope máximo aplicado" : ""}</div>
                      : units > 0 && <div className="text-[10px] text-red-500/80 leading-tight">{units} {unitWord} de atraso · {rule}{capped ? " · tope máximo aplicado" : ""}</div>}
                  </div>
                );
              })()}
              {data.overdue && <div className="text-[11px] font-semibold text-red-600 pt-1" data-testid="parent-overdue-flag">⚠ Saldo vencido — realiza tu pago para evitar más recargos.</div>}
            </div>
          )}

          {/* Pago por transferencia directa + comprobante */}
          <div className="mb-5 pb-5 border-b border-nardo-100" data-testid="parent-transfer-section">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-navy mb-3">
              <Building2 className="w-3.5 h-3.5" /> Pago por transferencia
            </div>
            {accounts.length === 0 ? (
              <p className="text-[12px] text-nardo-400">El colegio aún no ha registrado cuentas de depósito.</p>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {accounts.map((a) => (
                    <div key={a.id} className="bg-nardo-50 rounded-md p-3 text-sm" data-testid="deposit-account">
                      <div className="font-semibold text-navy">{a.bank}</div>
                      {a.holder && <div className="text-[12px] text-nardo-600">Titular: {a.holder}</div>}
                      {a.clabe && <CopyableField label="CLABE" value={a.clabe} />}
                      {a.account && <CopyableField label="Cuenta" value={a.account} />}
                      {a.reference && <div className="text-[12px] text-nardo-600">Referencia: {a.reference}</div>}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-nardo-500 mb-3">Transfiere a la cuenta del colegio poniendo como concepto el que elijas abajo, y adjunta tu comprobante.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={trConcept} onChange={(e) => onTrConcept(e.target.value)} data-testid="transfer-concept-select"
                    className="border border-nardo-300 rounded-md bg-white text-navy text-sm py-2 px-3 focus:border-navy outline-none">
                    <option value="">Concepto…</option>
                    <option>Colegiatura</option>
                    {concepts.map((c) => (<option key={c.id} value={c.name}>{c.name}{c.amount > 0 ? ` · ${money(c.amount)}` : ""}</option>))}
                    <option>Inscripción</option>
                    <option>Otro</option>
                  </select>
                  <input value={trAmount} onChange={(e) => setTrAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                    placeholder="Monto (MXN)" inputMode="decimal" data-testid="transfer-amount-input" max={maxAmt > 0 ? maxAmt : undefined}
                    className={`w-32 border-b-2 bg-transparent py-2 px-1 focus:border-navy outline-none text-navy text-sm ${maxAmt > 0 && parseFloat(trAmount) > maxAmt ? "border-red-400" : "border-nardo-200"}`} />
                </div>
                {maxAmt > 0 && (
                  <p className={`text-[11px] mt-1 ${parseFloat(trAmount) > maxAmt ? "text-red-500 font-medium" : "text-nardo-400"}`} data-testid="transfer-max-hint">
                    Monto máximo por pago: {money(maxAmt)}.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button type="button" onClick={() => trRef.current?.click()} data-testid="transfer-file-btn"
                    className="inline-flex items-center gap-1.5 border border-nardo-300 text-nardo-700 hover:border-navy px-3 py-2 rounded-md text-sm font-medium transition-colors">
                    <Upload className="w-4 h-4" /> {trFile ? "Comprobante listo ✓" : "Adjuntar comprobante"}
                  </button>
                  <input ref={trRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setTrFile(e.target.files?.[0] || null)} />
                  <button type="button" onClick={submitTransfer} disabled={trBusy} data-testid="transfer-submit"
                    className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
                    {trBusy ? "Enviando…" : "Enviar pago"}
                  </button>
                </div>
                {trFile && <p className="text-[11px] text-nardo-500 mt-1 truncate">{trFile.name}</p>}
                <p className="text-[11px] text-nardo-400 mt-2">Tu pago quedará <strong>pendiente de verificación</strong> hasta que el colegio lo confirme.</p>
              </>
            )}
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-2">Historial de pagos</div>
          {data.payments.length === 0 ? (
            <div className="py-4 text-center text-sm text-nardo-400">Aún no hay pagos registrados.</div>
          ) : (
            <div className="divide-y divide-nardo-100">
              {data.payments.map((p) => (
                <div key={p.folio || p.proof_token} className="py-2.5 flex items-center justify-between text-sm" data-testid="parent-pay-row">
                  <div>
                    <div className="text-navy font-medium">{p.concept}{p.period ? ` · ${p.period}` : ""}</div>
                    <div className="text-[11px] text-nardo-400 font-mono">{p.folio || "—"} · {p.date ? new Date(p.date).toLocaleDateString("es-MX") : ""} · {p.method}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono ${p.status === "pending_verification" ? "text-orange-500" : p.status === "rejected" ? "text-red-500 line-through" : "text-emerald-700"}`}>{money(p.net_amount)}</div>
                    {p.status === "pending_verification" && <div className="inline-flex items-center gap-1 text-[10px] text-orange-500"><Clock className="w-3 h-3" /> Por verificar</div>}
                    {p.status === "rejected" && <div className="text-[10px] text-red-500">Rechazado</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-[11px] text-nardo-400">Realiza tu transferencia a la cuenta del colegio y sube tu comprobante. Dirección lo verificará y quedará registrado en tu estado de cuenta.</p>
        </>
      )}
    </div>
  );
}

const PICKUP_MODES = [
  { id: "auto", label: "En auto", Icon: Car },
  { id: "pie", label: "A pie", Icon: Footprints },
  { id: "moto", label: "En moto", Icon: Bike },
];
const PICKUP_STATUS = {
  recibido: { label: "Recibido — el colegio ya vio tu solicitud", color: "text-navy", dot: "bg-navy", bg: "bg-navy/5 border-navy/20" },
  preparando: { label: "Preparando a tu hijo/a para la salida", color: "text-orange-600", dot: "bg-orange-500", bg: "bg-orange-50 border-orange-200" },
  entregado: { label: "¡Entregado! Tu hijo/a ya salió con éxito", color: "text-emerald-600", dot: "bg-emerald-500", bg: "bg-emerald-50 border-emerald-200" },
};

function PickupSection({ token, name, preview }) {
  const [pickup, setPickup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("auto");
  const [eta, setEta] = useState("");
  const [plates, setPlates] = useState("");
  const [carColor, setCarColor] = useState("");
  const [busy, setBusy] = useState(false);
  const [newSalida, setNewSalida] = useState(false);

  const headers = { "X-Parent-Token": token };
  const load = useCallback(async () => {
    try { const { data } = await api.get("/parent/pickup", { headers }); setPickup(data.pickup); }
    catch { /* silencio */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setLoading(true); load();
    const id = setInterval(load, 7000);
    return () => clearInterval(id);
  }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/parent/pickup", { mode, eta, plates, car_color: carColor }, { headers });
      setPickup(data);
      setNewSalida(false);
      toast.success("Salida programada. El colegio fue notificado.");
    } catch { toast.error("No se pudo programar la salida"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!pickup) return;
    setBusy(true);
    try { await api.delete(`/parent/pickup/${pickup.id}`, { headers }); setPickup(null); toast.success("Solicitud cancelada"); }
    catch { toast.error("No se pudo cancelar"); }
    finally { setBusy(false); }
  };

  const active = pickup && (pickup.status === "recibido" || pickup.status === "preparando");
  const delivered = pickup && pickup.status === "entregado";
  const st = pickup ? PICKUP_STATUS[pickup.status] : null;
  const showStatus = active || (delivered && !newSalida);

  return (
    <div className="bg-white rounded-xl border border-nardo-200 p-5 sm:p-6 mb-6" data-testid="parent-pickup">
      <div className="flex items-center gap-2 mb-4">
        <LogOut className="w-5 h-5 text-navy" />
        <h3 className="font-display font-bold text-lg text-navy">Área de entrega · Salida</h3>
      </div>

      {loading ? (
        <div className="text-sm text-nardo-400 py-3">Cargando…</div>
      ) : showStatus ? (
        <div className={`rounded-xl border p-4 ${st.bg}`} data-testid="pickup-status-card">
          <div className={`flex items-center gap-2 font-semibold ${st.color}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${st.dot} ${active ? "animate-pulse" : ""}`} />
            {st.label}
          </div>
          <div className="text-sm text-nardo-600 mt-3 grid grid-cols-2 gap-2">
            <div><span className="text-nardo-400">Medio:</span> {PICKUP_MODES.find((m) => m.id === pickup.mode)?.label || pickup.mode}</div>
            {pickup.eta && <div><span className="text-nardo-400">Hora:</span> {pickup.eta}</div>}
            {pickup.plates && <div><span className="text-nardo-400">Placas:</span> {pickup.plates}</div>}
            {pickup.car_color && <div><span className="text-nardo-400">Color:</span> {pickup.car_color}</div>}
          </div>
          {active && !preview && (
            <button onClick={cancel} disabled={busy} data-testid="pickup-cancel" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-red-600 hover:text-red-700 disabled:opacity-50">
              <X className="w-3.5 h-3.5" /> Cancelar salida
            </button>
          )}
          {delivered && !preview && (
            <button onClick={() => setNewSalida(true)} data-testid="pickup-new" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-ink text-white py-2.5 rounded-xl font-semibold text-sm transition-colors">
              <LogOut className="w-4 h-4" /> Programar nueva salida
            </button>
          )}
        </div>
      ) : (
        <div data-testid="pickup-form">
          <p className="text-sm text-nardo-500 mb-4">Avisa al colegio cómo pasarás por {name?.split(" ")[0] || "tu hijo/a"}. La dirección recibirá la notificación al instante.</p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {PICKUP_MODES.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setMode(id)} data-testid={`pickup-mode-${id}`}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${mode === id ? "bg-navy text-white border-navy" : "bg-white text-navy border-nardo-200 hover:border-navy"}`}>
                <Icon className="w-6 h-6" /> <span className="text-xs font-medium">{label}</span>
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-xs text-nardo-500 block mb-1">Hora estimada</label>
              <input type="time" value={eta} onChange={(e) => setEta(e.target.value)} data-testid="pickup-eta" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
            </div>
            <div>
              <label className="text-xs text-nardo-500 block mb-1">Placas {mode !== "auto" && "(opcional)"}</label>
              <input value={plates} onChange={(e) => setPlates(e.target.value.toUpperCase())} placeholder="ABC-123" data-testid="pickup-plates" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm uppercase outline-none focus:border-navy" />
            </div>
            <div>
              <label className="text-xs text-nardo-500 block mb-1">Color</label>
              <input value={carColor} onChange={(e) => setCarColor(e.target.value)} placeholder="Blanco" data-testid="pickup-color" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
            </div>
          </div>
          <button onClick={submit} disabled={busy || preview} data-testid="pickup-submit" className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-ink text-white py-3 rounded-xl font-semibold disabled:opacity-50 transition-colors">
            <LogOut className="w-4 h-4" /> Programar salida
          </button>
        </div>
      )}
    </div>
  );
}

const pMoney = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dueDateLabel = (iso) => { if (!iso) return ""; const d = new Date(iso + "T00:00:00"); return d.toLocaleDateString("es-MX", { day: "numeric", month: "long" }); };

function PaymentAlertBanner({ stmt }) {
  if (!stmt) return null;
  const balance = stmt.balance || 0;
  const days = stmt.days_to_due; // puede ser null si Dirección no configuró fecha
  const paidUp = stmt.paid_up || balance <= 0;
  const overdue = balance > 0 && ((days != null && days < 0) || stmt.overdue);
  const goPay = () => document.querySelector('[data-testid="parent-statement-section"]')?.scrollIntoView({ behavior: "smooth", block: "center" });

  if (paidUp) {
    return (
      <div className="rounded-xl p-4 mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200" data-testid="payment-banner" data-state="ok">
        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
        <div><div className="font-display font-bold text-emerald-800">¡Felicidades! Estás al corriente 🎉</div>
          <div className="text-xs text-emerald-700">No tienes adeudos pendientes este mes. ¡Gracias!</div></div>
      </div>
    );
  }
  if (overdue) {
    return (
      <div className="rounded-xl overflow-hidden mb-6 border border-red-300 shadow-lg animate-pulse" data-testid="payment-banner" data-state="overdue">
        <div className="p-4 flex items-center gap-3 bg-red-600 text-white">
          <AlertCircle className="w-7 h-7 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">Se venció tu pago · se están cobrando recargos</div>
            <div className="text-sm text-red-100">Adeudo actual: {pMoney(balance)}{stmt.late_fee > 0 ? ` (incluye ${pMoney(stmt.late_fee)} de recargo)` : ""}. Actualiza tu pago ahora.</div>
          </div>
          <button onClick={goPay} data-testid="payment-banner-cta" className="shrink-0 bg-white text-red-600 font-bold text-sm px-4 py-2 rounded-lg hover:bg-red-50">Pagar ahora</button>
        </div>
      </div>
    );
  }
  if (days != null && days >= 0 && days <= 10) {
    return (
      <div className="rounded-xl overflow-hidden mb-6 border border-orange-300 shadow-lg" data-testid="payment-banner" data-state="due-soon">
        <div className="p-4 flex items-center gap-3 bg-orange-500 text-white">
          <CalendarClock className="w-7 h-7 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">
              {days === 0 ? "Hoy vence tu pago" : `Faltan ${days} día${days === 1 ? "" : "s"} para el vencimiento de tu pago`}
            </div>
            <div className="text-sm text-orange-50">Vence el {dueDateLabel(stmt.due_date)} · Adeudo: {pMoney(balance)}</div>
          </div>
          <button onClick={goPay} data-testid="payment-banner-cta" className="shrink-0 bg-white text-orange-600 font-bold text-sm px-4 py-2 rounded-lg hover:bg-orange-50">Pagar</button>
        </div>
        <div className="bg-orange-600 text-white text-center text-xs font-semibold py-1.5 animate-pulse">⚠️ Después se cobrarán recargos</div>
      </div>
    );
  }
  if (days != null && days > 10) {
    return (
      <div className="rounded-xl p-4 mb-6 flex items-center gap-3 bg-navy/5 border border-navy/20" data-testid="payment-banner" data-state="upcoming">
        <CalendarClock className="w-6 h-6 text-navy-light shrink-0" />
        <div><div className="font-display font-bold text-navy">Tu próximo pago vence en {days} días</div>
          <div className="text-xs text-nardo-600">Vence el {dueDateLabel(stmt.due_date)} · Adeudo: {pMoney(balance)}</div></div>
      </div>
    );
  }
  // Pendiente sin fecha configurada por Dirección
  return (
    <div className="rounded-xl overflow-hidden mb-6 border border-orange-300 shadow" data-testid="payment-banner" data-state="pending">
      <div className="p-4 flex items-center gap-3 bg-orange-500 text-white">
        <AlertCircle className="w-6 h-6 shrink-0" />
        <div className="flex-1 min-w-0"><div className="font-display font-extrabold">Tienes un pago pendiente</div>
          <div className="text-sm text-orange-50">Adeudo actual: {pMoney(balance)}</div></div>
        <button onClick={goPay} data-testid="payment-banner-cta" className="shrink-0 bg-white text-orange-600 font-bold text-sm px-4 py-2 rounded-lg hover:bg-orange-50">Pagar</button>
      </div>
    </div>
  );
}

const AULA_TYPE_META = {
  tarea: { label: "Tarea", icon: ClipboardCheck, badge: "bg-navy text-white" },
  anuncio: { label: "Aviso", icon: Megaphone, badge: "bg-amber-500 text-white" },
  material: { label: "Material", icon: FolderOpen, badge: "bg-emerald-600 text-white" },
};
const aulaYtId = (u) => { const m = (u || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/); return m ? m[1] : null; };
const aulaDue = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d) ? null : d.toLocaleString("es-MX", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); };
const SUB_STATE = {
  entregado: { label: "Entregada", cls: "bg-emerald-100 text-emerald-700" },
  revisado: { label: "Revisada", cls: "bg-sky-100 text-sky-700" },
  calificado: { label: "Calificada", cls: "bg-navy/10 text-navy" },
};

function AulaTaskEntry({ post, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(post.submission?.note || "");
  const fileRef = useRef(null);
  const sub = post.submission;
  const graded = sub?.status === "calificado";
  const headers = { "X-Parent-Token": token };

  const submit = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (note.trim()) fd.append("note", note.trim());
      await api.post(`/parent/aula/submit/${post.id}`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Entrega enviada al docente");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo entregar"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeFile = async (t) => {
    setBusy(true);
    try { await api.delete(`/parent/aula/submit/${post.id}/file/${t}`, { headers }); toast.success("Archivo quitado"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo quitar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/40" data-testid={`aula-entry-${post.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] text-navy-light flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Tu entrega</div>
        {sub?.status && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SUB_STATE[sub.status]?.cls || "bg-nardo-100 text-nardo-600"}`} data-testid={`aula-sub-status-${post.id}`}>{SUB_STATE[sub.status]?.label || sub.status}</span>}
      </div>

      {sub?.files?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {sub.files.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-navy/5 text-navy rounded-lg px-2.5 py-1.5">
              <FileText className="w-3.5 h-3.5" />
              <a href={`${API}/aula/archivo/${f.token}`} target="_blank" rel="noreferrer" className="truncate max-w-[9rem] hover:underline">{f.name}</a>
              {!graded && <button onClick={() => removeFile(f.token)} disabled={busy} className="text-nardo-400 hover:text-red-600" data-testid={`aula-rmfile-${post.id}-${i}`}><X className="w-3.5 h-3.5" /></button>}
            </span>
          ))}
        </div>
      )}

      {graded ? (
        <div className="rounded-lg bg-navy/5 p-3">
          {sub.grade != null && <div className="text-sm text-navy font-semibold flex items-center gap-1.5"><Award className="w-4 h-4 text-navy-light" /> Calificación: {sub.grade}</div>}
          {sub.feedback && <div className="text-[12px] text-nardo-600 mt-1">Comentario del docente: {sub.feedback}</div>}
        </div>
      ) : (
        <div className="space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota para el docente (opcional)…" data-testid={`aula-note-${post.id}`} className="w-full text-sm border border-nardo-200 rounded-lg px-3 py-2 outline-none focus:border-navy resize-none" />
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs inline-flex items-center gap-1.5 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-3 py-2 cursor-pointer transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Adjuntar archivo
              <input ref={fileRef} type="file" className="hidden" data-testid={`aula-file-${post.id}`} onChange={(e) => e.target.files?.[0] && submit(e.target.files[0])} />
            </label>
            <button onClick={() => submit(null)} disabled={busy || (!note.trim() && !(sub?.files?.length))} data-testid={`aula-submit-note-${post.id}`} className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50">
              <Send className="w-4 h-4" /> Enviar entrega
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AulaPostCard({ p, token, preview, open, onToggle, onChanged }) {
  const meta = AULA_TYPE_META[p.type] || AULA_TYPE_META.tarea;
  const due = aulaDue(p.due_at);
  const subSt = p.type === "tarea" && p.submission?.status ? SUB_STATE[p.submission.status] : null;
  return (
    <div className="rounded-xl border border-nardo-200/70 bg-white/60 overflow-hidden" data-testid={`parent-aula-post-${p.id}`}>
      <button type="button" onClick={onToggle} data-testid={`parent-aula-toggle-${p.id}`} aria-expanded={open}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/80 transition-colors">
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full ${meta.badge}`}><meta.icon className="w-3 h-3" /> {meta.label}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-navy truncate">{p.title}</div>
          <div className="text-[11px] text-nardo-400 truncate">{p.docente_name}{due ? ` · Entrega: ${due}` : ""}</div>
        </div>
        {subSt && <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${subSt.cls}`}>{subSt.label}</span>}
        <ChevronDown className={`w-4 h-4 text-nardo-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          {p.description && <p className="text-sm text-nardo-600 whitespace-pre-wrap leading-relaxed">{p.description}</p>}
          {due && <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1"><CalendarClock className="w-3.5 h-3.5" /> Entrega: {due}</div>}
          {p.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {p.attachments.map((a, i) => (
                <a key={i} href={a.href ? `${API}${a.href}` : `${API}/aula/archivo/${a.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] bg-navy/5 hover:bg-navy/10 text-navy rounded-lg px-2.5 py-1.5 transition-colors"><FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[10rem]">{a.name}</span> <Download className="w-3 h-3 text-nardo-400" /></a>
              ))}
            </div>
          )}
          {p.links?.length > 0 && (
            <div className="flex flex-col gap-1 mt-2">
              {p.links.map((l, i) => <a key={i} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-navy-light hover:text-navy underline break-all"><ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l}</a>)}
            </div>
          )}
          {p.youtube?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {p.youtube.map((y, i) => { const id = aulaYtId(y); return id ? (
                <div key={i} className="aspect-video rounded-xl overflow-hidden bg-black/5"><iframe title={`yt-${i}`} src={`https://www.youtube.com/embed/${id}`} className="w-full h-full" allowFullScreen frameBorder="0" /></div>
              ) : <a key={i} href={y} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:underline"><Youtube className="w-4 h-4" /> {y}</a>; })}
            </div>
          )}
          {p.type === "tarea" && !preview && <AulaTaskEntry post={p} token={token} onChanged={onChanged} />}
          {p.type === "tarea" && preview && <div className="mt-3 pt-3 border-t border-white/40 text-[11px] text-nardo-400">La entrega de archivos está disponible para el tutor.</div>}
        </div>
      )}
    </div>
  );
}

const aulaDayLabel = (day) => {
  const d = new Date(day + "T12:00:00");
  if (isNaN(d)) return day;
  const s = d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function ClubSection({ token }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    api.get("/parent/clubs", { headers: { "X-Parent-Token": token } }).then(({ data }) => setData(data)).catch(() => setData({ clubs: [], items: [] }));
  }, [token]);
  if (!data || !data.clubs.length) return null;
  const fileUrl = (a) => `${BACKEND_URL}/api/parent/club-file/${a.file_token}?t=${encodeURIComponent(token)}`;
  return (
    <div className="bg-white/70 backdrop-blur rounded-2xl border border-white/60 shadow-lg mt-6 overflow-hidden" data-testid="parent-club-section">
      <button onClick={() => setOpen((o) => !o)} data-testid="parent-club-toggle" className="w-full flex items-center gap-3 p-5 sm:p-6 text-left">
        <Megaphone className="w-5 h-5 text-navy-light" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg text-navy">Avisos de club</h3>
          <div className="text-xs text-nardo-500 truncate">{data.clubs.map((c) => c.name).join(" · ")}</div>
        </div>
        {data.items.length > 0 && <span className="min-w-[24px] h-6 px-2 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center">{data.items.length}</span>}
        <ChevronDown className={`w-5 h-5 text-navy transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-5 space-y-2" data-testid="parent-club-list">
          {data.items.length === 0 ? <p className="text-sm text-nardo-500 py-4 text-center">Aún no hay avisos del club.</p> : data.items.map((a) => (
            <div key={a.id} className="rounded-xl border border-nardo-200 bg-white" data-testid={`parent-club-item-${a.id}`}>
              <button onClick={() => {
                const opening = openId !== a.id; setOpenId(opening ? a.id : null);
                if (opening) api.post(`/parent/clubs/${a.id}/seen`, {}, { headers: { "X-Parent-Token": token } }).catch(() => {});
              }} className="w-full text-left px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-navy-light font-semibold">{a.club_name}</div>
                <div className="font-semibold text-navy text-sm">{a.title}</div>
                <div className="text-[11px] text-nardo-500">{a.created_by_name} · {a.created_at ? new Date(a.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
              </button>
              {openId === a.id && (
                <div className="px-4 pb-4 text-sm text-nardo-700 whitespace-pre-wrap">
                  {a.body}
                  {a.file_token && <a href={fileUrl(a)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-navy font-semibold underline" data-testid={`parent-club-file-${a.id}`}><Paperclip className="w-4 h-4" /> {a.file_name || "Ver archivo"}</a>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AulaSection({ token, preview }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/parent/aula/posts", { headers: { "X-Parent-Token": token } })
      .then(({ data }) => { if (alive) setPosts(data.posts || []); })
      .catch(() => { if (alive) setPosts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  // Agrupar por día (más reciente primero) — queda como registro histórico.
  const groups = React.useMemo(() => {
    const map = new Map();
    for (const p of posts) {
      const day = (p.created_at || "").slice(0, 10) || "—";
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(p);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [posts]);

  return (
    <div className="bg-white/70 backdrop-blur rounded-2xl border border-white/60 shadow-lg p-5 sm:p-6 mt-6" data-testid="parent-aula-section">
      <div className="flex items-center gap-2 mb-4">
        <Presentation className="w-5 h-5 text-navy-light" />
        <h3 className="font-display font-bold text-lg text-navy">Aula Virtual</h3>
      </div>
      {loading ? (
        <div className="py-10 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : posts.length === 0 ? (
        <div className="py-10 text-center text-nardo-500 text-sm" data-testid="parent-aula-empty">Todavía no hay tareas ni avisos del grupo.</div>
      ) : (
        <div className="space-y-6">
          {groups.map(([day, items]) => (
            <div key={day} data-testid={`parent-aula-day-${day}`}>
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className="w-3.5 h-3.5 text-navy-light" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-navy">{aulaDayLabel(day)}</span>
                <span className="text-[10px] text-nardo-400">({items.length})</span>
                <span className="flex-1 h-px bg-nardo-200/70" />
              </div>
              <div className="space-y-2">
                {items.map((p) => (
                  <AulaPostCard key={p.id} p={p} token={token} preview={preview}
                    open={openId === p.id}
                    onToggle={() => setOpenId((cur) => {
                      const next = cur === p.id ? null : p.id;
                      if (next === p.id && !preview) {
                        api.post(`/parent/aula/posts/${p.id}/seen`, {}, { headers: { "X-Parent-Token": token } }).catch(() => {});
                      }
                      return next;
                    })}
                    onChanged={() => setReload((k) => k + 1)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function PortalPadres() {
  const previewToken = new URLSearchParams(window.location.search).get("preview");
  const preview = !!previewToken;
  const [children, setChildren] = useState(preview ? [] : loadChildren());
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [locked, setLocked] = useState(() => !preview && localStorage.getItem("uaicard_parent_locked") === "1");

  useEffect(() => { if (!preview) saveChildren(children); }, [children, preview]);
  useEffect(() => { if (!preview) localStorage.setItem("uaicard_parent_locked", locked ? "1" : "0"); }, [locked, preview]);

  // Modo vista previa (admin): carga el alumno por token, sin persistir
  useEffect(() => {
    if (!previewToken) return;
    (async () => {
      try {
        const { data } = await api.get("/parent/me", { headers: { "X-Parent-Token": previewToken } });
        setChildren([{ token: previewToken, student: data.student }]);
        setActive(0);
      } catch { toast.error("Vista previa no disponible"); }
    })();
  }, [previewToken]);

  const addChild = useCallback((child, isAdding) => {
    setChildren((prev) => {
      if (!isAdding) {
        // Inicio de sesión con un UID: muestra SOLO a ese alumno (exclusivo).
        // No conserva alumnos de sesiones/códigos anteriores.
        setActive(0);
        return [child];
      }
      // "Agregar hijo/a": guarda el alumno junto a los ya registrados (o lo actualiza).
      const exists = prev.findIndex((c) => c.student.student_id === child.student.student_id);
      const next = exists >= 0 ? prev.map((c, i) => (i === exists ? child : c)) : [...prev, child];
      setActive(exists >= 0 ? exists : next.length - 1);
      return next;
    });
    setAdding(false);
    setLocked(false);
  }, []);

  const removeChild = (idx) => {
    setChildren((prev) => prev.filter((_, i) => i !== idx));
    setActive(0);
  };

  const current = children[active];
  const [attData, setAttData] = useState(null);
  const [stmtData, setStmtData] = useState(null);
  const [theme, setTheme] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  // Tema del colegio (con stock por defecto). Se aplica colores; el fondo usa theme.theme_bg (o stock).
  useEffect(() => {
    if (!current) { setTheme(null); setPaymentInfo(null); resetThemeToStock(); return; }
    let on = true;
    api.get("/parent/me", { headers: { "X-Parent-Token": current.token } })
      .then((r) => { if (on) { setTheme({ ...(r.data.theme || {}), watermark_url: r.data.watermark_url }); setPaymentInfo(r.data.payment_info || { deposit_accounts: [], fee_concepts: [] }); } })
      .catch(() => {});
    return () => { on = false; };
  }, [current?.token]);

  useEffect(() => {
    const t = theme || {};
    applyTheme(t.theme_palette, t.theme_btn, {
      title: t.theme_title_color, body: t.theme_text_color, btn: t.theme_btn_text_color,
    }, t.theme_logo_colors, t.theme_panel_color, t.theme_custom_color);
  }, [theme]);

  useEffect(() => {
    if (!current) { setAttData(null); setStmtData(null); return; }
    let on = true;
    const headers = { "X-Parent-Token": current.token };
    const fetchAll = () => {
      api.get("/parent/attendance", { headers }).then((r) => { if (on) setAttData(r.data); }).catch((e) => console.error(e));
      api.get("/parent/statement", { headers }).then((r) => { if (on) setStmtData(r.data); }).catch((e) => console.error(e));
    };
    setAttData(null); setStmtData(null);
    fetchAll();
    // Auto-actualización: al volver a la app/pestaña y cada 60s, sin recargar manualmente.
    const onFocus = () => { if (document.visibilityState === "visible") fetchAll(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const iv = setInterval(() => { if (document.visibilityState === "visible") fetchAll(); }, 60000);
    return () => {
      on = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(iv);
    };
  }, [current?.token, reloadKey]);

  return (
    <div className="min-h-screen bg-nardo-50 relative">
      <LogoSplash sessionKey="padres" />
      <ThemedBackground bgId={theme?.theme_bg} speed={theme?.theme_bg_speed} watermarkUrl={theme && theme.watermark_url ? `${API}${theme.watermark_url}` : null} />
      <div className="relative z-10">
      <div className="h-[3px] w-full bg-navy" />
      <header className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/zavia-icon.png" alt="ZAVIA" data-testid="zavia-icon" className="w-9 h-9 object-contain shrink-0" />
          <div className="leading-none min-w-0">
            <div className="font-display font-extrabold tracking-tight text-[15px] text-navy truncate">Portal de Padres</div>
            <div className="font-display tracking-[0.3em] text-[10px] uppercase text-nardo-500">{theme?.portal_subtitle || "ZAVIA"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!preview && current?.token && !locked && (
            <NotificationCenter parentToken={current.token} tone="light" />
          )}
          {!preview && current?.token && !locked && (
            <PushToggle parentToken={current.token} compact />
          )}
          {!preview && children.length > 0 && !locked && (
            <button onClick={() => { setLocked(true); setAdding(false); }} data-testid="parent-logout" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy">
              <KeyRound className="w-3.5 h-3.5" /> Salir
            </button>
          )}
          {preview ? (
            <button onClick={() => window.close()} data-testid="parent-preview-close" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy">
              <ArrowLeft className="w-3.5 h-3.5" /> Cerrar
            </button>
          ) : (
            <Link to="/" data-testid="parent-back-home" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy">
              <ArrowLeft className="w-3.5 h-3.5" /> Inicio
            </Link>
          )}
        </div>
      </header>

      {preview && (
        <div className="bg-navy text-white text-center text-xs uppercase tracking-[0.25em] py-2" data-testid="parent-preview-banner">
          Vista previa — así ve el tutor este portal (solo lectura)
        </div>
      )}

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
        {!preview && (children.length === 0 || adding || locked) ? (
          <div className="flex justify-center pt-8 relative" data-testid="parent-portal">
            <AddChildForm onAdded={addChild} onCancel={() => setAdding(false)} canCancel={adding && !locked} adding={adding && !locked} />
          </div>
        ) : !current ? (
          <div className="py-24 text-center text-nardo-400" data-testid="parent-portal">Cargando…</div>
        ) : (
          <div data-testid="parent-portal">
            {/* Tabs por hijo (seccionado) */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pt-2 pb-4">
              {children.map((c, i) => (
                <button
                  key={c.student.student_id}
                  onClick={() => setActive(i)}
                  data-testid={`parent-child-tab-${i}`}
                  className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${i === active ? "bg-navy text-white" : "bg-white text-navy border border-nardo-200 hover:border-navy"}`}
                >
                  {c.student.has_photo
                    ? <img src={`${API}/parent/photo?pt=${encodeURIComponent(c.token)}`} alt="" className="w-6 h-6 rounded-full object-cover" />
                    : <span aria-hidden className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${i === active ? "bg-white/20" : "bg-nardo-100"}`}>{c.student.full_name?.[0] || "A"}</span>}
                  {c.student.full_name}
                </button>
              ))}
              {!preview && (
                <button onClick={() => setAdding(true)} data-testid="parent-add-child" className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm text-nardo-500 hover:text-navy border border-dashed border-nardo-300 hover:border-navy transition-colors">
                  <Plus className="w-4 h-4" /> Agregar hijo/a
                </button>
              )}
            </div>

            {current && !preview && <PushPrompt parentToken={current.token} />}
            {current && (
              <>
                <div className="bg-navy text-white rounded-xl p-5 sm:p-6 mb-6 flex items-center gap-4" data-testid="parent-child-header">
                  {current.student.has_photo
                    ? <img src={`${API}/parent/photo?pt=${encodeURIComponent(current.token)}`} alt="" className="w-16 h-16 rounded-xl object-cover border border-white/20" />
                    : <div aria-hidden className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center font-display font-extrabold text-2xl">{current.student.full_name?.[0] || "A"}</div>}
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-extrabold text-2xl truncate">{current.student.full_name}</div>
                    {current.student.tutor_label && (
                      <div className="text-nardo-300 text-xs mt-0.5" data-testid="parent-tutor-label">Tutor: {current.student.tutor_label}</div>
                    )}
                    <div className="text-nardo-300 text-sm flex items-center gap-2 mt-0.5"><GraduationCap className="w-4 h-4" /> {current.student.grade}{current.student.level ? ` · ${current.student.level}` : ""} · {current.student.school_name}</div>
                  </div>
                  {!preview && (
                    <button onClick={() => removeChild(active)} data-testid="parent-remove-child" title="Quitar" className="text-nardo-300 hover:text-white p-2">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <PaymentAlertBanner stmt={stmtData} />

                <SummaryCard att={attData} stmt={stmtData} />

                <PickupSection token={current.token} name={current.student.full_name} preview={preview} />

                <div className="grid lg:grid-cols-2 gap-6">
                  <AttendanceSection data={attData} token={current.token} name={current.student.full_name} />
                  <StatementSection data={stmtData} token={current.token} paymentInfo={paymentInfo} onDone={() => setReloadKey((k) => k + 1)} />
                </div>

                <StudentBadges token={current.token} compact />

                <ClubSection token={current.token} />

                <AulaSection token={current.token} preview={preview} />

                <ComedorSection token={current.token} name={current.student.full_name} />

                {!preview && (
                  <div className="mt-6 space-y-6">
                    <StudentTemarioPanel token={current.token} />
                    <StudentResourcesPanel token={current.token} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <SiteFooter />
      </div>
      </div>
    </div>
  );
}

const CM_CONCEPT = { desayuno: "Desayuno", comida: "Comida", completo: "Completo" };
const CM_MODALITY = { dia: "Por día", mensual: "Mensual" };
const cmMoney = (v) => "$" + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CM_DAYS = [{ k: "lun", l: "Lunes" }, { k: "mar", l: "Martes" }, { k: "mie", l: "Miércoles" }, { k: "jue", l: "Jueves" }, { k: "vie", l: "Viernes" }];

function ComedorSection({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/parent/comedor", { headers: { "X-Parent-Token": token } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData({ orders: [], charges: [], pending_total: 0, menu: { days: {} } }); });
    return () => { alive = false; };
  }, [token]);

  if (!data) return null;
  const menu = data.menu || { days: {} };
  const hasMenu = Object.values(menu.days || {}).some((d) => (d.desayuno || d.comida));
  const pendientes = (data.charges || []).filter((c) => c.status !== "pagado");
  const hasAnything = hasMenu || (data.charges || []).length || (data.orders || []).length;
  if (!hasAnything) return null;

  return (
    <div className="mt-6 bg-white rounded-xl border border-nardo-200 overflow-hidden" data-testid="parent-comedor">
      <div className="px-5 py-3 bg-navy/5 border-b border-nardo-100 flex items-center gap-2">
        <UtensilsCrossed className="w-5 h-5 text-navy" />
        <h3 className="font-display font-bold text-navy">Comedor</h3>
      </div>
      <div className="p-5 space-y-5">
        {/* Adeudos */}
        {data.pending_total > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="parent-comedor-adeudo">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-red-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Adeudo de comedor</span>
              <span className="text-lg font-display font-extrabold text-red-700">{cmMoney(data.pending_total)}</span>
            </div>
            <ul className="mt-2 space-y-1">
              {pendientes.map((c) => (
                <li key={c.id} className="text-xs text-red-800/80 flex justify-between"><span>{c.month}{c.concept ? ` · ${CM_CONCEPT[c.concept] || c.concept}` : ""}{c.note ? ` · ${c.note}` : ""}</span><span className="font-semibold">{cmMoney(c.amount)}</span></li>
              ))}
            </ul>
          </div>
        )}

        {/* Menú semanal */}
        {hasMenu && (
          <div data-testid="parent-comedor-menu">
            <div className="text-sm font-semibold text-navy mb-2">{menu.title || "Menú de la semana"}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {CM_DAYS.filter((d) => (menu.days[d.k]?.desayuno || menu.days[d.k]?.comida)).map((d) => (
                <div key={d.k} className="rounded-lg border border-nardo-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-navy-light font-bold mb-1">{d.l}</div>
                  {menu.days[d.k]?.desayuno && <div className="text-xs text-nardo-600"><b>Desayuno:</b> {menu.days[d.k].desayuno}</div>}
                  {menu.days[d.k]?.comida && <div className="text-xs text-nardo-600"><b>Comida:</b> {menu.days[d.k].comida}</div>}
                </div>
              ))}
            </div>
            {menu.note && <p className="text-[11px] text-nardo-400 mt-2">{menu.note}</p>}
          </div>
        )}

        {/* Comprobantes */}
        {(data.orders || []).length > 0 && (
          <div data-testid="parent-comedor-comprobantes">
            <div className="text-sm font-semibold text-navy mb-2">Mis comprobantes</div>
            <div className="divide-y divide-nardo-100 border border-nardo-200 rounded-lg">
              {data.orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between px-3 py-2" data-testid={`parent-comedor-order-${o.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-navy font-medium truncate">{CM_CONCEPT[o.concept] || o.concept} · {CM_MODALITY[o.modality] || o.modality}</div>
                    <div className="text-[11px] text-nardo-400">{o.date} · Folio {o.folio}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-emerald-700">{cmMoney(o.amount)}</span>
                    <button onClick={() => downloadPdf(`/parent/comedor/receipt/${o.id}.pdf`, { params: { pt: token }, filename: `comprobante_comedor_${o.date}.pdf` })} data-testid={`parent-comedor-pdf-${o.id}`} className="inline-flex items-center gap-1 text-xs border border-emerald-500/40 text-emerald-700 hover:bg-emerald-600 hover:text-white px-2.5 py-1.5 rounded-lg font-semibold transition-colors"><Download className="w-3.5 h-3.5" /> PDF</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
